import React, { Suspense, useEffect, useState, useSyncExternalStore, useCallback, useLayoutEffect } from 'react';
const { createPortal, flushSync }: { flushSync: (callback: () => void) => void; createPortal: (children: React.ReactNode, container: Element, key: string) => React.ReactNode } = require('react-dom');
import { Provider } from 'react-redux';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { previewSourceStyle, previewSectionStyle } from './style';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { store } from '../../store';
import { AppearancePreviewContext } from '../../theme/AppearanceContext';
import AppearanceTypographyProvider from '../../theme/AppearanceTypographyProvider';
import { ScreenElementStyleScope } from '../ScreenElementStyleScope';
import { ElementStyleScope } from '../EditableElement';
import { ElementTypographyContext } from '../ElementTypography';
import { ExistingPreviewContext, PreviewEnvironmentContext } from './PreviewContext';
import { previewScreens } from './screens.web';
import { previewProject, previewResponse } from './fixtures';
import loadIcons from '../../icons';
import contract from '../contract.json';
import type { PreviewBridge } from './ExistingPreview.web';

loadIcons();
const bridge: PreviewBridge = (window.frameElement as any).studioPreviewBridge;
const Stack = createStackNavigator();
const params = { projectId: 1, forceShow: true, project: previewProject, conversationId: 'studio-conversation', sessionId: 'studio-session', id: 1, previewQuestion: previewResponse('/projects/next-carplay-question/1/studio-user/studio-user', 'GET', { scenario: bridge.scenario }).payload };

export class PreviewErrorBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { bridge.reportError('This screen preview could not render.'); }
  render() { return this.state.failed ? null : this.props.children; }
}
export default function PreviewHost() {
  const [active, setActive] = useState(true);
  useLayoutEffect(() => bridge.registerDisposal(() => flushSync(() => setActive(false))), []);
  useSyncExternalStore(bridge.subscribe, bridge.version, bridge.version);
  useEffect(() => {
    bridge.registerOverlayRoot(document.body);
    const reportOverlay = () => { bridge.reportOverlay(!!document.querySelector('[aria-modal="true"]')); bridge.refresh(); };
    const observer = new MutationObserver(reportOverlay);
    observer.observe(document.body, { childList: true, subtree: true });
    reportOverlay();
    return () => { observer.disconnect(); bridge.registerOverlayRoot(null); };
  }, []);
  useEffect(() => {
    // RN-web inserts CSS with CSSOM, which MutationObserver cannot observe.
    let previous = '';
    const copyStyles = () => {
      const css = [...document.styleSheets].flatMap(sheet => { try { return [...sheet.cssRules].map(rule => rule.cssText); } catch { return []; } }).join('\n');
      if (css !== previous) { previous = css; bridge.styles(css); }
    };
    copyStyles(); const timer = window.setInterval(copyStyles, 100);
    return () => window.clearInterval(timer);
  }, []);
  return active ? <Provider store={store}><AppearancePreviewContext.Provider value={bridge.theme}><AppearanceTypographyProvider>
    <SafeAreaProvider initialMetrics={{ frame: { x: 0, y: 0, width: bridge.width, height: 1200 }, insets: { top: 0, bottom: 0, left: 0, right: 0 } }}>
      <PreviewEnvironmentContext.Provider value={{ scenario: bridge.scenario, platform: ['account-siri', 'account-watch', 'account-health-data', 'plan-emergency-sos', 'plan-health-alerts'].includes(bridge.slug) ? 'ios' : 'web' }}><PreviewScreen key={bridge.slug} /></PreviewEnvironmentContext.Provider>
    </SafeAreaProvider>
  </AppearanceTypographyProvider></AppearancePreviewContext.Provider></Provider> : null;
}

export function PreviewScreen() {
  const [hasSections, setHasSections] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const sectionMounted = useCallback(() => setHasSections(true), []);
  const ready = useCallback(() => setLoaded(true), []);
  const [sourceContainer] = useState(() => { const element = document.createElement('div'); element.setAttribute('data-studio-screen-source', bridge.slug); return element; });
  const firstSlot = bridge.slots.values().next().value;
  useLayoutEffect(() => { if (firstSlot && sourceContainer.parentNode !== firstSlot) firstSlot.appendChild(sourceContainer); }, [firstSlot, sourceContainer]);
  useEffect(() => () => sourceContainer.remove(), [sourceContainer]);
  const Screen = previewScreens[bridge.slug as keyof typeof previewScreens] as React.ComponentType<any> | undefined;
  const ownedSections: readonly string[] = contract.existingScreens[bridge.slug as keyof typeof contract.existingScreens]?.sections || [];
  const renderSection = (name: string, children: React.ReactNode) => ownedSections.includes(name) ? <PortaledSection name={name} onMount={sectionMounted}>{children}</PortaledSection> : <>{children}</>;
  useEffect(() => {
    if (!Screen) { bridge.reportError('This screen has no registered preview renderer.'); return; }
    if (!loaded) return;
    const report = () => bridge.reportStatus({ phase: 'ready', sections: populatedPreviewSections(bridge.slots, hasSections) });
    report();
    const observer = new MutationObserver(report);
    bridge.slots.forEach(slot => observer.observe(slot, { childList: true, subtree: true, characterData: true }));
    return () => observer.disconnect();
  }, [Screen, loaded, hasSections, bridge.version()]);
  if (!Screen) return null;
  const screen = <div style={previewSourceStyle(bridge.width, hasSections, bridge.interactive)} data-studio-screen-source={bridge.slug}>
    <ScreenElementStyleScope slug={bridge.slug} styles={bridge.styleScopes.values().next().value?.styles || {}}><NavigationContainer><Stack.Navigator screenOptions={{ headerShown: false, animation: 'none' }}>
      <Stack.Screen name="Sample" component={Screen} initialParams={params} />
    </Stack.Navigator></NavigationContainer></ScreenElementStyleScope>
  </div>;
  return <ExistingPreviewContext.Provider value={renderSection}><PreviewErrorBoundary><Suspense fallback={null}>
    {createPortal(<>{screen}<PreviewReady onReady={ready} /></>, sourceContainer, 'screen-source')}
  </Suspense></PreviewErrorBoundary></ExistingPreviewContext.Provider>;
}

function PreviewReady({ onReady }: { onReady: () => void }) {
  useEffect(onReady, [onReady]);
  return null;
}

export function populatedPreviewSections(slots: Map<string, HTMLElement>, detached: boolean): string[] {
  return [...slots].filter(([, slot]) => [...slot.childNodes].some(child => {
    if (detached && child.nodeType === 1 && (child as Element).hasAttribute('data-studio-screen-source')) return false;
    if (child.textContent?.trim()) return true;
    return child.nodeType === 1 && ((child as Element).matches('input, textarea, select, img, svg, canvas, video') || !!(child as Element).querySelector('input, textarea, select, img, svg, canvas, video'));
  })).map(([name]) => name);
}

function PortaledSection({ name, onMount, children }: { name: string; onMount: () => void; children: React.ReactNode }) {
  useEffect(onMount, []);
  const slot = bridge.slots.get(name);
  const definition = contract.components[name as keyof typeof contract.components];
  const content = definition && 'fill' in definition && definition.fill && React.Children.toArray(children).length ? <div style={previewSectionStyle}>{children}</div> : children;
  return slot ? createPortal(<ElementTypographyContext.Provider value={bridge.elementStyles.get(name) || {}}><ElementStyleScope value={bridge.styleScopes.get(name)?.styles || {}} scope={bridge.styleScopes.get(name)?.scope || name}>{content}</ElementStyleScope></ElementTypographyContext.Provider>, slot, name) : null;
}
